import React from "react";
import {
  Grid,
  makeStyles,
  Typography,
  Paper,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  Checkbox,
} from "@material-ui/core";
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import useError from "Hooks/useError";
import MuiAlert from "@material-ui/lab/Alert";
import { message } from "constants/message";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(2),
  },
  messsageWrapper: {
    marginTop: theme.spacing(1),
  },
}));

export default function Form({ submit, user = null, isEdit = false }) {
  const classes = useStyles();
  const stateError = useError();
  const messages = message();
  const [adminName, setAdminName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [features, setFeatures] = React.useState({
    administrator: false,
    account: true,
    csvReport: true,
    customer: true,
    info: true,
    informationUrls: true,
    collectionTheme: true,
    dtm: true,
    dtmSetting: true,
    mediaAccounts: true,
    mediaCategories: true,
    log: true,
  });

  const { 
    administrator, 
    account, 
    csvReport, 
    customer, 
    info, 
    informationUrls, 
    collectionTheme,
    dtm, 
    dtmSetting, 
    mediaAccounts, 
    mediaCategories, 
    log, 
  } = features;

  const handleChange = (event) => {
    setFeatures({ ...features, [event.target.name]: event.target.checked });
  };

  React.useEffect(() => {
    stateError.initialize();
  }, []);

  React.useEffect(() => {
    if (!user) return;
    setAdminName(user.adminName);
    setEmail(user.email);
    setPassword(user.adminName);
    setDesc(user.desc);
    setFeatures(user.features);
  }, [user]);

  // パスワード入力形式確認用バリデーションを追加
  ValidatorForm.addValidationRule('checkPassword', (value) => {
    if (!value) return true; 
    // パスワードの正規表現
    //   (?=.*?[\u0021-\u002f\u003a-\u0040\u005b-\u0060\u007b-\u007e]) # lookahead punctuations
    //   (?=.*?[\u0030-\u0039])                                        # lookahead digits
    //   (?=.*?[\u0041-\u005a])                                        # lookahead upper case alphabets
    //   (?=.*?[\u0061-\u007a])                                        # lookahead lower case alphabets
    //   [\u0021-\u007e]{8,32}                                         # 8 to 32 of ascii printables
    const regexp = /^((?=.*?[\u0021-\u002f\u003a-\u0040\u005b-\u0060\u007b-\u007e])(?=.*?[\u0030-\u0039])(?=.*?[\u0041-\u005a])(?=.*?[\u0061-\u007a])([\u0021-\u007e]{8,32}))$/;

    if (!regexp.test(value)) {
      return false;
    } else {
      return true;
    }
  });

  const onError = () => {
    stateError.dispatch({
      type: "set",
      payload: {
        isError: true,
        errorMsg: messages.validation.common,
      },
    });
  };

  const checkboxError = 
    [administrator, account, csvReport, customer, info, informationUrls, collectionTheme, dtmSetting, mediaAccounts, mediaCategories, log].filter((v) => v).length === 0;

  return (
    <Grid>
      <ValidatorForm
        onSubmit={(e) => {
          e.preventDefault();
          submit({ adminName, desc, email, password, features });
        }}
        onError={onError}
      >
        <Paper elevation={3} className={classes.paper}>
          <Typography variant="h6" color="primary">
            {isEdit ? "管理者更新" : "管理者登録"}
          </Typography>
          <Grid container>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="管理者名*"
                fullWidth
                value={adminName}
                onChange={(event) => {
                  setAdminName(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required,
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="管理者概要*"
                fullWidth
                value={desc}
                onChange={(event) => {
                  setDesc(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required, 
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="管理者アドレス*"
                fullWidth
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                type="email"
                validators={['required', 'isEmail']}
                errorMessages={[
                  messages.validation.required, 
                  messages.validation.emailRegexp, 
                ]}
              />
            </Grid>
            {!isEdit && (
              <Grid item xs={12} className={classes.wrapper}>
                <TextValidator
                  label="パスワード*"
                  fullWidth
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                  type="password"
                  autoComplete="new-password"
                  validators={['required', 'checkPassword']}
                  errorMessages={[
                    messages.validation.required,
                    messages.validation.isPasswordRegexp
                  ]}
                />
              </Grid>
            )}
            <Grid item xs={12} className={classes.wrapper}>
              <FormControl required error={checkboxError} component="fieldset">
                <FormLabel>利用可能機能</FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={<Checkbox checked={administrator} onChange={handleChange} name="administrator" />}
                    label="管理者管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={account} onChange={handleChange} name="account" />}
                    label="アカウント管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={csvReport} onChange={handleChange} name="csvReport" />}
                    label="CSVダウンロード"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={customer} onChange={handleChange} name="customer" />}
                    label="顧客データ調査"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={info} onChange={handleChange} name="info" />}
                    label="お知らせ管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={informationUrls} onChange={handleChange} name="informationUrls" />}
                    label="ヘッダー告知管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={collectionTheme} onChange={handleChange} name="collectionTheme" />}
                    label="絞り込みテーマ管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={dtm} onChange={handleChange} name="dtm" />}
                    label="DTM告知管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={dtmSetting} onChange={handleChange} name="dtmSetting" />}
                    label="DTM告知一括設定"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={mediaAccounts} onChange={handleChange} name="mediaAccounts" />}
                    label="多メディア収集グループ管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={mediaCategories} onChange={handleChange} name="mediaCategories" />}
                    label="多メディア管理"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={log} onChange={handleChange} name="log" />}
                    label="操作ログ"
                  />
                </FormGroup>
                <FormHelperText>１つ以上選択してください。</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {stateError.state.isError && (
          <div className={classes.messsageWrapper}>
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              className={classes.toast}
            >
              {stateError.state.errorMsg}
            </MuiAlert>
          </div>
        )}
        <Grid container justifyContent="center" className={classes.wrapper}>
          <Button type="submit" variant="contained" color="primary">
            {isEdit ? "更新" : "登録"}
          </Button>
        </Grid>
      </ValidatorForm>
    </Grid>
  );
}
